/**
 * Platform Configuration
 * Central config for business lines, workspaces, status labels, and branding.
 * Single source of truth for platform-level settings.
 */

// ─── Lead Status Type ───
export type LeadStatus =
  | 'new' | 'contacted' | 'consulting' | 'quotation_sent' | 'hospital_selected'
  | 'trip_ready' | 'in_progress' | 'arrived' | 'treatment_completed' | 'revenue_confirmed'
  | 'cancelled' | 'failed' | 'no_show' | 'duplicate';

// ─── Status Display Labels ───
export const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'Lead Created',
  contacted: 'Contacted',
  consulting: 'Medical Qualified',
  quotation_sent: 'Prelim. Quotation',
  hospital_selected: 'Hospital Selected',
  trip_ready: 'Trip Ready',
  in_progress: 'Trip Planned',
  arrived: 'Arrived in Vietnam',
  treatment_completed: 'Treatment Completed',
  revenue_confirmed: 'Financial Verified',
  cancelled: 'Cancelled',
  failed: 'Failed',
  no_show: 'No Show',
  duplicate: 'Duplicate',
};

// Short labels for compact UI (funnel bars, table columns)
export const STATUS_SHORT_LABELS: Record<LeadStatus, string> = {
  new: 'Created',
  contacted: 'Contacted',
  consulting: 'Qualified',
  quotation_sent: 'Quotation',
  hospital_selected: 'Hospital',
  trip_ready: 'Trip Ready',
  in_progress: 'Trip Plan',
  arrived: 'Arrived',
  treatment_completed: 'Completed',
  revenue_confirmed: 'Fin. Verified',
  cancelled: 'Cancelled',
  failed: 'Failed',
  no_show: 'No Show',
  duplicate: 'Duplicate',
};

// ─── Status Groupings ───
export const PIPELINE_STATUSES: LeadStatus[] = [
  'new', 'contacted', 'consulting', 'quotation_sent', 'hospital_selected', 'trip_ready', 'in_progress', 'arrived', 'treatment_completed',
];

export const FINANCIAL_STATUSES: LeadStatus[] = ['revenue_confirmed'];

export const LOSS_STATUSES: LeadStatus[] = ['cancelled', 'failed', 'no_show', 'duplicate'];

export const ALL_STATUSES: LeadStatus[] = [...PIPELINE_STATUSES, ...FINANCIAL_STATUSES, ...LOSS_STATUSES];

// ─── Workspaces ───
export interface Workspace {
  id: string;
  name: string;
  businessLine: string;
  enabled: boolean;
  isDefault: boolean;
  description?: string;
}

export const WORKSPACES: Workspace[] = [
  {
    id: 'inbound_sea',
    name: 'Cross-Border Care',
    businessLine: 'medtour',
    enabled: true,
    isDefault: true,
    description: 'Inbound SEA medical tourism',
  },
  {
    id: 'outbound_vn_hnwi',
    name: 'Private Medical Care',
    businessLine: 'medtour',
    enabled: false,
    isDefault: false,
    description: 'Coming soon',
  },
];

// ─── Platform Branding ───
export const PLATFORM = {
  businessLine: 'MedTour',
  name: 'MedTour Platform',
  adminTitle: 'MedTour Admin',
  defaultWorkspace: 'inbound_sea',
} as const;

// ─── Role Visibility ───
export type UserRole = 'admin' | 'agent' | 'ops' | 'bod_readonly' | 'vendor' | 'hospital';

export interface NavItemConfig {
  path: string;
  label: string;
  roles: UserRole[];
  icon?: string;
  group?: string;
  /** Module key for permission gating (my_module_permissions). */
  moduleKey?: string;
}

export interface NavGroupConfig {
  id: string;
  label: string;
  roles: UserRole[];
  items: NavItemConfig[];
}

export const ADMIN_NAV_GROUPS: NavGroupConfig[] = [
  {
    id: 'overview',
    label: 'Overview',
    roles: ['admin', 'ops', 'bod_readonly'],
    items: [
      { path: '/admin/dashboard', moduleKey: 'dashboard', label: 'Dashboard', roles: ['admin', 'ops', 'bod_readonly'], icon: 'LayoutDashboard' },
    ],
  },
  {
    id: 'crm',
    label: 'CRM / Sales',
    roles: ['admin', 'ops', 'bod_readonly'],
    items: [
      { path: '/admin/leads', moduleKey: 'leads', label: 'Leads', roles: ['admin', 'ops', 'bod_readonly'], icon: 'Users' },
      { path: '/admin/lead-submissions', moduleKey: 'lead_submissions', label: 'Lead Submissions', roles: ['admin', 'ops', 'bod_readonly'], icon: 'ClipboardList' },
      { path: '/admin/quotes', moduleKey: 'quotes', label: 'Quotes', roles: ['admin', 'ops', 'bod_readonly'], icon: 'FileText' },
    ],
  },
  {
    id: 'patient_db',
    label: 'Guest Database',
    roles: ['admin', 'ops', 'bod_readonly'],
    items: [
      { path: '/admin/guests', moduleKey: 'guests', label: 'Guests', roles: ['admin', 'ops', 'bod_readonly'], icon: 'Heart' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    roles: ['admin', 'ops', 'bod_readonly'],
    items: [
      { path: '/admin/operations', moduleKey: 'operations_center', label: 'Operations Center', roles: ['admin', 'ops', 'bod_readonly'], icon: 'Compass' },
      { path: '/admin/vendors', moduleKey: 'vendors', label: 'Vendors', roles: ['admin', 'ops', 'bod_readonly'], icon: 'Building2' },
      // Retired from navigation; retained for reference:
      // { path: '/admin/coordination-fees', moduleKey: 'coordination_fees', label: 'Coordination Fees', roles: ['admin'], icon: 'Handshake' },
      { path: '/admin/packages', moduleKey: 'packages', label: 'Packages', roles: ['admin'], icon: 'Package' },
      { path: '/admin/pricing-seasons', moduleKey: 'pricing_seasons', label: 'Pricing Seasons', roles: ['admin'], icon: 'CalendarRange' },
    ],
  },
  {
    id: 'admin_tools',
    label: 'Admin Tools',
    roles: ['admin'],
    items: [
      { path: '/admin/settings', moduleKey: 'settings', label: 'Settings', roles: ['admin'], icon: 'Settings' },
    ],
  },
];

// Legacy flat config (backward compat)
export const ADMIN_NAV_CONFIG: NavItemConfig[] = ADMIN_NAV_GROUPS.flatMap(g => g.items);

// ─── Vendor portal modules ───
/**
 * Which modules the Vendor portal exposes. Hotels confirm bookings by email,
 * freelance interpreters and tour operators over Zalo / Telegram / email, so
 * Stays, Interpreters and Tours are confirmed by KHV staff on the trip case
 * instead (Confirm button). Only transport runs through the portal.
 * Flip to `true` to re-open a module (e.g. once hotel partners have their own
 * system or volume justifies it) — pages and data are kept intact.
 */
export const VENDOR_PORTAL_MODULES = {
  arrivals: true,
  departures: true,
  daily: true,
  tours: false,
  stays: false,
  interpreters: false,
  changes: true,
} as const;

export type VendorPortalModule = keyof typeof VENDOR_PORTAL_MODULES;

// ─── Guest portal features ───
/**
 * Self-service change requests in the Guest portal (transport / stay / tour
 * changes, extra rental days, Requests tab). Off: guests call the KHV hotline,
 * where Khmer-speaking staff take the change and ops update the trip case.
 * Flip to `true` to re-open (pages, hooks and data are kept intact).
 */
export const GUEST_PORTAL_FEATURES = {
  changeRequests: false,
} as const;

/** KHV support contact shown to guests. Fill in `phone` (E.164) to show a tap-to-call link. */
export const KHV_GUEST_SUPPORT: { phone: string; label: string } = {
  phone: '+84 836 111 768',
  label: 'KHV hotline (call / Zalo / Telegram)',
};

/** Legal entity block printed on customer documents (Booking Confirmation template). */
export const SINVCO_COMPANY = {
  name: 'SMART INVESTMENT CONSULTING JOINT STOCK COMPANY',
  address: '204/3 Nguyen Van Huong Street, An Khanh Ward, Ho Chi Minh City, Vietnam',
  taxCode: '0318508243',
  hotline: '+84 836 111 768',
  email: 'info@sinvco.com',
  website: 'www.khmerhealthvn.com',
} as const;
